import { useEffect, useState } from 'react'
import { tournamentApi } from '../services/api'
import { useLanguage } from '../context/LanguageContext'
import Toggle from './Toggle'
import { RESELLER_MENU_ITEMS, DEFAULT_HIDDEN_NAV } from '../config/resellerMenu'

export function defaultManagedInviteMessage(parentName, t) {
  if (t) {
    return t(
      'sp_invite_default_message',
      '{parentName} has set up a ready-to-run Play and Stay storefront for you. Accept this invitation to start taking bookings -- the packages, courses, hotels and pricing are already configured. You can manage the day-to-day, and {parentName} can keep helping behind the scenes.',
      { parentName }
    )
  }
  return `${parentName} has set up a ready-to-run Play and Stay storefront for you. ` +
    `Accept this invitation to start taking bookings -- the packages, courses, ` +
    `hotels and pricing are already configured. You can manage the day-to-day, ` +
    `and ${parentName} can keep helping behind the scenes.`
}

/**
 * Parent-managed child invite modal (EPIC #92, P3).
 *   course:   single groupingId fixed (from grouping tile)
 *   reseller: parent picks one or more groupings
 */
export default function ManagedInviteModal({
  open,
  onClose,
  onSuccess,
  inviteType,
  groupingId,
  groupings = [],
  parentName = 'Your partner',
}) {
  const { t } = useLanguage()
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [message, setMessage] = useState('')
  const [hotelMarkup, setHotelMarkup] = useState('0')
  const [golfMarkup, setGolfMarkup] = useState('0')
  // Which portal menu items are HIDDEN from this child. Seeded with the
  // default hidden set (Connected courses / API access / Custom domain); the
  // parent can show/hide any item.
  const [hiddenKeys, setHiddenKeys] = useState(() => new Set(DEFAULT_HIDDEN_NAV))
  const [selectedIds, setSelectedIds] = useState([])
  const [submitting, setSubmitting] = useState(false)
  const [err, setErr] = useState(null)

  useEffect(() => {
    if (!open) return
    setEmail('')
    setName('')
    setMessage(defaultManagedInviteMessage(parentName, t))
    setErr(null)
    setHiddenKeys(new Set(DEFAULT_HIDDEN_NAV))
    if (inviteType === 'reseller') {
      setSelectedIds(groupings.map(g => g.ID_NO))
    } else {
      setSelectedIds(groupingId ? [groupingId] : [])
    }
  }, [open, inviteType, groupingId, groupings, parentName])

  if (!open) return null

  const courseGrouping = inviteType === 'course'
    ? groupings.find(g => g.ID_NO === groupingId) || { Name: t('sp_invite_this_grouping', 'this grouping') }
    : null

  function toggleGrouping(id) {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    )
  }

  function selectAll() {
    setSelectedIds(groupings.map(g => g.ID_NO))
  }

  function clearAll() {
    setSelectedIds([])
  }

  function toggleVisible(key, visible) {
    setHiddenKeys(prev => {
      const next = new Set(prev)
      if (visible) next.delete(key)
      else next.add(key)
      return next
    })
  }

  async function submit(e) {
    e.preventDefault()
    setErr(null)
    setSubmitting(true)
    try {
      const body = {
        inviteType,
        email: email.trim(),
        name: name.trim() || undefined,
        message: message.trim() || undefined,
        hotelMarkupPercent: Number(hotelMarkup) || 0,
        golfMarkupPercent: Number(golfMarkup) || 0,
        hiddenNavKeys: [...hiddenKeys],
      }
      if (inviteType === 'course') {
        body.groupingId = groupingId
      } else {
        body.groupingIds = selectedIds
      }
      const { data } = await tournamentApi.post('/portal/reseller/invites', body)
      onSuccess?.(data)
      onClose()
    } catch (e2) {
      setErr(e2?.response?.data?.message || e2.message)
    } finally {
      setSubmitting(false)
    }
  }

  const title = inviteType === 'course'
    ? t('sp_invite_course_title', 'Invite a course to run "{name}"', { name: courseGrouping?.Name })
    : t('sp_invite_reseller_title', 'Invite a reseller to run your storefront')

  return (
    <div style={overlay} onClick={onClose}>
      <div style={modalCard} onClick={ev => ev.stopPropagation()}>
        <h3 style={{ margin: '0 0 8px', fontSize: 18, color: '#0f172a' }}>{title}</h3>
        <p style={{ margin: '0 0 20px', fontSize: 13, color: '#64748b', lineHeight: 1.5 }}>
          {inviteType === 'course'
            ? t('sp_invite_course_intro', 'The course will receive a link to accept and run this grouping. Packages and pricing stay as you configured them.')
            : t('sp_invite_reseller_intro', 'Pick which groupings to hand over. The invitee gets a ready-made account with those destinations.')}
        </p>

        {err && <div style={errorBox}>{err}</div>}

        <form onSubmit={submit}>
          {inviteType === 'reseller' && (
            <div style={{ marginBottom: 18 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <label style={label}>{t('sp_invite_groupings_to_include', 'Groupings to include')}</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button" onClick={selectAll} style={linkBtn}>{t('sp_invite_select_all', 'Select all')}</button>
                  <button type="button" onClick={clearAll} style={linkBtn}>{t('sp_invite_clear', 'Clear')}</button>
                </div>
              </div>
              {groupings.length === 0
                ? <div style={mutedBox}>{t('sp_invite_no_groupings_create_first', 'No groupings yet. Create at least one grouping first.')}</div>
                : (
                  <div style={checkList}>
                    {groupings.map(g => (
                      <label key={g.ID_NO} style={checkRow}>
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(g.ID_NO)}
                          onChange={() => toggleGrouping(g.ID_NO)}
                        />
                        <span>
                          <strong>{g.Name}</strong>
                          <span style={{ color: '#64748b', fontSize: 12, marginLeft: 8 }}>{g.RadiusKm}km</span>
                        </span>
                      </label>
                    ))}
                  </div>
                )}
            </div>
          )}

          <Field label={t('sp_invite_contact_email', 'Contact email')} hint={t('sp_invite_contact_email_hint', 'They will receive the invitation link at this address.')}>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              autoFocus
              style={inp}
              placeholder="manager@golfcourse.com"
            />
          </Field>

          <Field label={t('sp_invite_contact_name_optional', 'Contact name (optional)')}>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              style={inp}
              placeholder="Jane Smith"
            />
          </Field>

          <div style={{ marginBottom: 14, padding: 12, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8 }}>
            <label style={label}>{t('sp_invite_markup_label', 'Your markup')}</label>
            <p style={{ margin: '2px 0 10px', fontSize: 12, color: '#64748b', lineHeight: 1.4 }}>
              {t('sp_invite_markup_hint', "Added to this account's hotel and golf costs — your revenue. They never see it. Default 0%.")}
            </p>
            <div style={{ display: 'flex', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <div style={label}>{t('sp_invite_markup_hotel', 'Hotel markup (%)')}</div>
                <input type="number" min={0} max={100} step="0.01" value={hotelMarkup} onChange={e => setHotelMarkup(e.target.value)} style={inp} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={label}>{t('sp_invite_markup_golf', 'Golf markup (%)')}</div>
                <input type="number" min={0} max={100} step="0.01" value={golfMarkup} onChange={e => setGolfMarkup(e.target.value)} style={inp} />
              </div>
            </div>
          </div>

          <div style={{ marginBottom: 14, padding: 12, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8 }}>
            <label style={label}>{t('sp_invite_menu_label', 'Menu access')}</label>
            <p style={{ margin: '2px 0 10px', fontSize: 12, color: '#64748b', lineHeight: 1.4 }}>
              {t('sp_invite_menu_hint', 'Choose which menu items this account can see. A few advanced items are hidden by default — you can change any of them any time.')}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxHeight: 220, overflowY: 'auto', paddingRight: 4 }}>
              {RESELLER_MENU_ITEMS.map(item => (
                <Toggle
                  key={item.key}
                  size="sm"
                  checked={!hiddenKeys.has(item.key)}
                  onChange={(v) => toggleVisible(item.key, v)}
                  label={t(item.labelKey, item.fallback)}
                />
              ))}
            </div>
          </div>

          <Field label={t('sp_invite_personal_message', 'Personal message')} hint={t('sp_invite_personal_message_hint', 'Included in the invitation email. Edit as needed.')}>
            <textarea
              value={message}
              onChange={e => setMessage(e.target.value)}
              rows={5}
              style={{ ...inp, resize: 'vertical', minHeight: 100 }}
            />
          </Field>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
            <button type="button" onClick={onClose} style={secondaryBtn}>{t('cancel', 'Cancel')}</button>
            <button
              type="submit"
              disabled={submitting || (inviteType === 'reseller' && selectedIds.length === 0) || !email.trim()}
              style={{ ...primaryBtn, opacity: submitting ? 0.6 : 1 }}
            >
              {submitting ? t('sp_sending_ellipsis', 'Sending…') : t('sp_invite_send_invitation', 'Send invitation')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function Field({ label: labelText, hint, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={label}>{labelText}</div>
      {children}
      {hint && <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>{hint}</div>}
    </div>
  )
}

const overlay = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }
const modalCard = { background: '#fff', borderRadius: 12, padding: 28, width: '100%', maxWidth: 520, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 50px rgba(0,0,0,0.2)' }
const errorBox = { padding: 12, background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 6, marginBottom: 16, fontSize: 13 }
const inp = { width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box' }
const primaryBtn = { padding: '10px 20px', background: '#0f172a', color: '#fff', border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer' }
const secondaryBtn = { padding: '10px 16px', background: '#fff', color: '#475569', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 14, cursor: 'pointer' }
const label = { display: 'block', fontSize: 13, fontWeight: 600, color: '#1e293b', marginBottom: 6 }
const linkBtn = { background: 'none', border: 'none', color: '#2563eb', fontSize: 12, cursor: 'pointer', padding: 0 }
const checkList = { border: '1px solid #e2e8f0', borderRadius: 8, maxHeight: 180, overflowY: 'auto' }
const checkRow = { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderBottom: '1px solid #f1f5f9', fontSize: 14, cursor: 'pointer' }
const mutedBox = { padding: 12, background: '#f8fafc', borderRadius: 6, fontSize: 13, color: '#64748b' }
